import { capitalize, startCase } from "lodash";
import {
  DATE_PATTERN_REGEX,
  URL_PATTERN_REGEX,
} from "../../../../Forms/FormValidations";
import { dateFormatType, getFormattedDate } from "@SeemplicityDev/shared";
import React from "react";

export const MIN_SEARCH_LENGTH = 2;

const additionalDataFields: Record<string, string> = {
  last_analyzed_ts: "Last Analyzed",
  ai_analyst_priority: "AI analyst priority",
  ai_analyst_priority_direction: "AI analyst priority direction",
  has_ai_analysis: "Has AI analysis",
  analyst_instance_id: "Analyst instance",
  cve: "CVE",
  cwe: "CWE",
  os: "Operating System",
  url: "URL",
  ip: "IP",
  tracking_method: "Tracking Method",
  original_finding_id: "Original Finding ID",
  cvss_score: "CVSS Score",
  cvss_vector: "CVSS Vector",
  cvss_v2_score: "CVSS V2 Score",
  cvss_v2_temporal_score: "CVSS V2 Temporal Score",
  tenable_severity_score: "Tenable Severity",
  cvss_v2_vector: "CVSS V2 Vector",
  cvss_v3_score: "CVSS V3 Score",
  cvss_v3_vector: "CVSS V3 Vector",
  cvss_v3_temporal_score: "CVSS V3 Temporal Score",
  cvss_v4_score: "CVSS V4 Score",
  cvss_v4_vector: "CVSS V4 Vector",
  epss_score: "EPSS Score",
  epss_percentile: "EPSS Percentile",
  cisa_kev: "CISA KEV",
  vulncheck_kev: "VulnCheck KEV",
  rce: "RCE",
  unauthenticated_rce: "Unauthenticated RCE",
  tags_values: "Tags Values",
  sla_status: "SLA",
  finding_id: "Finding ID",
  resource_id: "Resource ID",
  sla_remaining_time: "SLA Remaining Time",
  cisa_kev_due_date: "CISA KEV Due Date",
  cwe_lists: "CWE Lists",
  fix_vendor_id: "Fix Vendor ID",
  fix_id: "Fix ID",
  fix_type: "Fix Type",
  finding_type_str: "Finding Type",
  fix_subtype: "Fix Subtype",
  rule_type: "Rule Type",
  rule_family: "Rule Family",
  traceable_detection_type: "Traceable Detection Type",
  mitigation_level: "EDR Mitigation",
  tags_key_values: "Tags Keys & Values",
  cpe: "CPE",
  ubuntu_esm_support: "Ubuntu ESM Support",
  actual_status: "Status",
  cloud_account: "Account",
  github_secret_validity: "GitHub Secret Validity",
  sonatype_waiver_exists: "SonaType Waiver Exists",
  system_status_key: "Source Mapped Status",
};

export const additionalDataTooltipFields: Record<string, string> = {
  cisa_kev:
    "Indicates if the vulnerability is listed in CISA's Known Exploited Vulnerabilities catalog.",
  vulncheck_kev:
    "Indicates if the vulnerability is listed in VulnCheck's Known Exploited Vulnerabilities catalog.",
  epss_percentile:
    "Shows how the EPSS Score ranks compared to all other vulnerabilities. Shown precisely above 70%; below that, an approximate range.",
  epss_score:
    "Estimates the probability (0%-100%) of the vulnerability being exploited in the next 30 days, provided by FIRST.",
  exploit_maturity:
    "Highest known exploit maturity level on the scale: Unknown, Proof of Concept, Exploitable, or Exploited in the Wild.",
};

export const formatEpssPercentile = (
  value: string | number | null | undefined
): string => {
  const n = Number(value);
  // null/undefined/non-finite are "no data" -> N/A. 0 is a real, meaningful
  // lowest-percentile reading and is shown exactly as "0%" (not the 0-10% band).
  if (value === null || value === undefined || !Number.isFinite(n)) {
    return "N/A";
  }

  if (n === 0) {
    return "0%";
  }

  if (n >= 95) {
    return `${n.toFixed(1)}%`;
  }

  if (n >= 70) {
    return `${Math.round(n)}%`;
  }

  // Below 70%, show an approximate 10-point band (e.g. 63 -> "60-70%").
  const lo = Math.floor(n / 10) * 10;
  const hi = lo + 10;
  return `${lo}-${hi}%`;
};

export const additionalDataFormatByField: Record<
  string,
  (value: string | number) => string
> = {
  epss_score: (value) => `${value}%`,
  epss_percentile: (value) => formatEpssPercentile(value),
  cvss_v2_score: (value) => parseFloat(String(value))?.toFixed(1),
  cvss_v3_score: (value) => parseFloat(String(value))?.toFixed(1),
  cvss_v4_score: (value) => parseFloat(String(value))?.toFixed(1),
  cvss_score: (value) => parseFloat(String(value))?.toFixed(1),
  cvss_v2_temporal_score: (value) => parseFloat(String(value))?.toFixed(1),
};

export const getKnownAdditionalDataTitle = (
  field: string
): string | undefined =>
  Object.hasOwn(additionalDataFields, field)
    ? additionalDataFields[field]
    : undefined;

export const getAdditionalDataTitle = (field: string): string =>
  getKnownAdditionalDataTitle(field) || startCase(field);

export const FIELDS_TO_EXCLUDE = [
  "epss_percentile",
  "epss_score",
  "package_version",
  "plugin_text",
  "malware_kit_count",
  "package",
  "cisa_kev_due_date",
];

export const GENERAL_FIELDS_SHOWN_IN_ADDITIONAL_DATA = ["original_status"];

export const formatContent = (
  content: unknown,
  field: string
): React.ReactNode => {
  if (Array.isArray(content)) {
    return content?.join(", ");
  }

  if (URL_PATTERN_REGEX.test(content as string)) {
    return (
      <a
        target={"_blank"}
        href={content as string}
        title={content as string}
        rel="noreferrer"
      >
        {content as string}
      </a>
    );
  }

  if (typeof content === "boolean") {
    return capitalize(content?.toString());
  }

  if (DATE_PATTERN_REGEX.test(content as string)) {
    return getFormattedDate(
      content as string,
      field?.toLowerCase()?.includes("time")
        ? dateFormatType.DEFAULT
        : dateFormatType.SHORT
    );
  }

  if (!!additionalDataFormatByField[field]) {
    return additionalDataFormatByField[field](content as string | number);
  }

  if (
    content === "" ||
    (Array.isArray(content) && (!content?.length || content?.[0] === ""))
  ) {
    content = "N/A";
  }

  return content as React.ReactNode;
};

export const searchField = (
  title: string,
  value: unknown,
  searchValue: string
): boolean => {
  if (searchValue === "" || searchValue?.length < MIN_SEARCH_LENGTH) {
    return true;
  }

  return (
    title.toLowerCase().includes(searchValue.toLowerCase()) ||
    (typeof value === "string"
      ? value?.toString().toLowerCase().includes(searchValue.toLowerCase())
      : (value as React.ReactElement<{ href?: string }>)?.props?.href
          ?.toLowerCase()
          .includes(searchValue.toLowerCase())) || // if the value isn't a string, it's a link object
    false
  );
};

export const openedByDefaultCategories = ["Vulnerability", "Package"];
